"use client";

import { useI18n } from "@/i18n/I18nProvider";
import { inputBox } from "./Row";
import type { FieldMeta } from "./fields";
import type { TableRow } from "./SettingsTable";
import { MultiSelect, Select } from "../Select";
import { Toggle } from "../ui";

type I18n = Pick<ReturnType<typeof useI18n>, "t" | "f">;

export type Value = Record<string, unknown>;

/** A nested object of settings (a group), as opposed to a value or a list. */
const isGroup = (v: unknown): v is Value => typeof v === "object" && v !== null && !Array.isArray(v);

/** A number as written: an integer plain (clock values like 1400 stay readable), else with its own decimals. */
const numberText = (f: I18n["f"], x: number) => (Number.isInteger(x) ? String(x) : f.num(x, Math.min(4, (String(x).split(".")[1] ?? "").length)));

/** A choice's label (the value itself when it is not among the choices). */
const optionText = (t: I18n["t"], m: FieldMeta, x: unknown) => {
  const o = m.options?.find((c) => c.value === String(x));
  return o ? t(o.label) : String(x);
};

/** A setting's value as text (its default in the table): a switch on / off, a choice, a list of choices, a number with its unit. */
function valueText({ t, f }: I18n, m: FieldMeta, v: unknown): string {
  if (v === undefined || v === null) return "—";
  if (typeof v === "boolean") return t(v ? "set.value.on" : "set.value.off");
  if (Array.isArray(v)) return v.map((x) => optionText(t, m, x)).join(", ");
  if (m.options) return optionText(t, m, v);
  if (typeof v !== "number") return String(v);
  return m.suffix ? `${numberText(f, v)} ${t(m.suffix)}` : numberText(f, v);
}

/** The settings of an object (a section or a group in it) and how to show and change them. */
interface Fields {
  value: Value;
  defaults: Value;
  /** The fields' presentation: by their key, in a group by `<group>.<key>`. */
  meta: Record<string, FieldMeta>;
  onChange: (v: Value) => void;
}

/** One setting of the object as a table row: label, description, input, default (`path`: its key in the meta). */
function settingRow(i: I18n, x: Fields, k: string, path: string): TableRow {
  const m = x.meta[path] ?? { label: path };
  const label = i.t(m.label);
  const v = x.value[k];
  const d = x.defaults[k];
  const set = (y: unknown) => x.onChange({ ...x.value, [k]: y });
  return { key: path, label, description: m.help ? i.t(m.help) : undefined, input: <FieldInput label={label} m={m} v={v} onChange={set} />, default: valueText(i, m, d), changed: d !== undefined && JSON.stringify(v) !== JSON.stringify(d), onDefault: () => set(d) };
}

/**
 * The flat settings of an object as table rows in their order — label, description, input, default. Lists are shown
 * when their choices are known (`options`); nested objects are left to their groups.
 */
export function settingRows(i: I18n, x: Fields & { skip?: string[] }): TableRow[] {
  const shown = ([k, v]: [string, unknown]) => !isGroup(v) && !(x.skip ?? []).includes(k) && (!Array.isArray(v) || !!x.meta[k]?.options);
  return Object.entries(x.value)
    .filter(shown)
    .map(([k]) => settingRow(i, x, k, k));
}

/** A group's settings (a nested object `group` of a section) as table rows, each field's presentation under `<group>.<field>`. */
export function groupRows(i: I18n, group: string, x: Fields): TableRow[] {
  return Object.keys(x.value).map((k) => settingRow(i, x, k, `${group}.${k}`));
}

/** The groups of a section shown as their own tables: the nested objects with a presentation for their fields. */
export function shownGroups(value: Value, meta: Record<string, FieldMeta>): string[] {
  const keys = Object.keys(meta);
  return Object.keys(value).filter((g) => isGroup(value[g]) && keys.some((k) => k.startsWith(`${g}.`)));
}

/** One setting's input by its kind: a switch, a list of choices, a choice, a number (with its unit) or text. */
function FieldInput({ label, m, v, onChange }: { label: string; m: FieldMeta; v: unknown; onChange: (v: unknown) => void }) {
  const { t } = useI18n();
  if (Array.isArray(v) && m.options) {
    const options = m.options.map((o) => ({ value: o.value, label: t(o.label) }));
    return <MultiSelect label={label} values={v as string[]} options={options} onChange={onChange} none={t("set.choice.none")} className="w-full" />;
  }
  if (typeof v === "boolean") return <Toggle on={v} label={label} onChange={onChange} />;
  if (m.options) {
    // a numeric choice stays a number
    const options = m.options.map((o) => ({ value: typeof v === "number" ? Number(o.value) : o.value, label: t(o.label) }));
    return <Select<string | number> label={label} value={v as string | number} options={options} onChange={onChange} className="w-full" />;
  }
  if (typeof v !== "number") return <input aria-label={label} className={inputBox} value={String(v)} onChange={(e) => onChange(e.target.value)} />;
  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      <input aria-label={label} className={inputBox} type="number" step={m.step ?? "any"} min={m.min} max={m.max} value={v} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />
      {m.suffix && <span className="shrink-0 text-[11px] text-muted">{t(m.suffix)}</span>}
    </div>
  );
}
